/**
 * Visual regression harness for the Client Workspace page.
 *
 * Renders the ClientWorkspacePage with mock data, bypassing authentication
 * and API calls. Accessible at /admin/client-workspace-visual-regression
 *
 * Mock data includes projects in various statuses, attention items,
 * and realistic timestamps for visual verification.
 */

import { useState, useEffect } from 'react';
import '../dashboard.css';
import { ClientWorkspacePage } from '../client-workspace/ClientWorkspacePage';
import { ToastProvider } from '../components/primitives/ToastProvider';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import * as clientProjectsService from '../../../services/clientProjectsService';
import type { ClientProjectSummaryDto } from '../../../types/client-workspace';

// --- Mock project data ---

const NOW = new Date();
const hoursAgo = (h: number) => new Date(NOW.getTime() - h * 3600000).toISOString();
const daysAgo = (d: number) => new Date(NOW.getTime() - d * 86400000).toISOString();

const MOCK_PROJECTS: ClientProjectSummaryDto[] = [
  {
    id: 'proj-001',
    name: 'Sunshade Louver Pergola Assembly',
    status: 'awaiting_review',
    lastActivityAt: hoursAgo(2),
    dueDate: daysAgo(-3), // 3 days from now
    priority: 8,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 2,
    hasUnreadMessages: true,
    hasNewActivity: true,
    collaboratorCount: 4,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-002',
    name: 'Industrial Motor Housing Guide',
    status: 'in_production',
    lastActivityAt: hoursAgo(5),
    dueDate: daysAgo(-14), // 14 days from now
    priority: 5,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: true,
    collaboratorCount: 3,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-003',
    name: 'EV Battery Pack Installation',
    status: 'revision_requested',
    lastActivityAt: hoursAgo(1),
    dueDate: daysAgo(-7),
    priority: 9,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: true,
    hasNewActivity: false,
    collaboratorCount: 6,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-004',
    name: 'Solar Panel Mounting System',
    status: 'blocked',
    lastActivityAt: daysAgo(2),
    dueDate: daysAgo(1), // overdue by 1 day
    priority: 7,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: false,
    collaboratorCount: 2,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-005',
    name: 'HVAC Ductwork Assembly',
    status: 'approved',
    lastActivityAt: daysAgo(1),
    dueDate: null,
    priority: 3,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: false,
    collaboratorCount: 2,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-006',
    name: 'Robotic Arm Calibration Guide',
    status: 'delivered',
    lastActivityAt: daysAgo(5),
    dueDate: null,
    priority: 4,
    deliveredAt: daysAgo(5),
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: false,
    collaboratorCount: 3,
    isRecentlyDelivered: true,
  },
  {
    id: 'proj-007',
    name: 'Warehouse Shelving Unit Assembly',
    status: 'waiting_on_client',
    lastActivityAt: daysAgo(3),
    dueDate: daysAgo(-10),
    priority: 2,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: false,
    collaboratorCount: 1,
    isRecentlyDelivered: false,
  },
  {
    id: 'proj-008',
    name: 'Commercial Kitchen Hood Installation',
    status: 'in_production',
    lastActivityAt: hoursAgo(12),
    dueDate: daysAgo(-21),
    priority: 6,
    deliveredAt: null,
    thumbnailUrl: null,
    pendingApprovals: 0,
    hasUnreadMessages: false,
    hasNewActivity: false,
    collaboratorCount: 4,
    isRecentlyDelivered: false,
  },
];

/**
 * Monkey-patches the clientProjectsService to return mock data
 * instead of making real API calls.
 */
function patchServiceWithMockData() {
  const mockResponse = {
    success: true as const,
    data: {
      projects: MOCK_PROJECTS,
      attentionItems: [],
      statusCounts: {
        all: MOCK_PROJECTS.length,
        in_production: MOCK_PROJECTS.filter(p => p.status === 'in_production').length,
        awaiting_review: MOCK_PROJECTS.filter(p => p.status === 'awaiting_review').length,
        revision_requested: MOCK_PROJECTS.filter(p => p.status === 'revision_requested').length,
        approved: MOCK_PROJECTS.filter(p => p.status === 'approved').length,
        delivered: MOCK_PROJECTS.filter(p => p.status === 'delivered').length,
        blocked: MOCK_PROJECTS.filter(p => p.status === 'blocked').length,
        waiting_on_client: MOCK_PROJECTS.filter(p => p.status === 'waiting_on_client').length,
      },
      lastLoadedAt: hoursAgo(24),
    },
  };

  // Override the service methods with mock implementations
  (clientProjectsService.clientProjectsService as any).listProjects = async () => mockResponse;
  (clientProjectsService.clientProjectsService as any).approveDeliverable = async () => ({
    success: true,
    data: { project: { ...MOCK_PROJECTS[0], status: 'approved' } },
  });
  (clientProjectsService.clientProjectsService as any).requestRevision = async () => ({
    success: true,
    data: { project: { ...MOCK_PROJECTS[0], status: 'revision_requested' } },
  });
  (clientProjectsService.clientProjectsService as any).dismissAttentionItem = async () => ({
    success: true,
  });
}

export function ClientWorkspaceVisualRegressionPage() {
  const [ready, setReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    patchServiceWithMockData();
    setReady(true);
  }, []);

  if (!ready) {
    return <div>Loading mock data...</div>;
  }

  return (
    <ToastProvider>
      <div
        data-testid="client-workspace-visual-regression-ready"
        data-scenario="client-workspace"
        className="flex h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_34%),linear-gradient(180deg,_#020617_0%,_#0f172a_100%)] text-slate-100"
      >
        {/* Sidebar — client role shows 3 items */}
        <DashboardSidebar
          activeSection="projects"
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onNavigate={() => {}}
          role="client"
        />

        {/* Main content area */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Minimal header bar */}
          <header className="flex h-16 shrink-0 items-center justify-between border-b border-white/5 px-6">
            <button
              className="text-slate-400 hover:text-white md:hidden"
              onClick={() => setSidebarOpen(true)}
              type="button"
            >
              ☰
            </button>
            <span className="text-sm font-medium text-slate-300">Client Workspace Preview</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Sarah Chen</span>
              <div className="size-8 rounded-full bg-sky-500/20 flex items-center justify-center text-xs text-sky-300 font-medium">SC</div>
            </div>
          </header>

          {/* Scrollable content */}
          <main className="flex-1 overflow-y-auto p-6">
            <ClientWorkspacePage
              role="client"
              displayName="Sarah Chen"
              onSignOut={async () => undefined}
              signOutError={null}
            />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
